import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';

export const HOME_TASK_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
};

const EVENTS_KEY = (email) => `liwaza-home-task-events-${email}`;
const SESSION_ARTICLE_PROGRESS_KEY = 'gre_vocab_article_progress';

/** Calendar day in the user's local timezone (not UTC). */
export const getLocalDateKey = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    return getLocalDateKey(new Date());
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseLocalDateKey = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateKey || ''));
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const dateForHomeProgramOffset = (
  anchorDate,
  offset = 0,
  windowDays = []
) => {
  const day = (windowDays || []).find(
    (item) => Number(item?.offset) === Number(offset)
  );
  const fromWindow = parseLocalDateKey(day?.date);
  if (fromWindow) return fromWindow;

  const anchor =
    anchorDate instanceof Date && !Number.isNaN(anchorDate.getTime())
      ? anchorDate
      : new Date();
  const shifted = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate()
  );
  const delta = Number(offset);
  shifted.setDate(shifted.getDate() + (Number.isFinite(delta) ? delta : 0));
  return shifted;
};

const getDateKey = getLocalDateKey;

const normalizeArticleId = (articleId) =>
  articleId == null ? null : String(articleId);

const readArticleProgressMap = (email) => {
  let localProgress = {};
  let sessionProgress = {};
  try {
    localProgress = JSON.parse(
      localStorage.getItem(`gre_vocab_article_progress_${email}`) || '{}'
    );
  } catch {
    localProgress = {};
  }
  try {
    sessionProgress = JSON.parse(
      sessionStorage.getItem(SESSION_ARTICLE_PROGRESS_KEY) || '{}'
    );
  } catch {
    sessionProgress = {};
  }
  return { ...sessionProgress, ...localProgress };
};

const getProgressForArticle = (allProgress, articleId) => {
  if (articleId == null) return {};
  const stringId = normalizeArticleId(articleId);
  return (
    allProgress[stringId] ||
    allProgress[articleId] ||
    allProgress[Number(stringId)] ||
    {}
  );
};

const readOpenedArticleIds = (email) => {
  const opened = new Set();
  const addIds = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((id) => {
      if (id != null) opened.add(normalizeArticleId(id));
    });
  };
  try {
    addIds(
      JSON.parse(
        localStorage.getItem(`gre_vocab_opened_articles_${email}`) || '[]'
      )
    );
  } catch {
    // ignore
  }
  try {
    addIds(
      JSON.parse(sessionStorage.getItem('gre_vocab_opened_articles') || '[]')
    );
  } catch {
    // ignore
  }
  return opened;
};

const readEvents = (email) => {
  if (!email) return {};
  try {
    return JSON.parse(localStorage.getItem(EVENTS_KEY(email)) || '{}');
  } catch {
    return {};
  }
};

const writeEvents = (email, events) => {
  if (!email) return;
  try {
    localStorage.setItem(EVENTS_KEY(email), JSON.stringify(events));
  } catch {
    // quota — non-critical
  }
};

export const markHomeTaskEvent = (email, taskId, event, date = new Date()) => {
  if (!email || !taskId) return;
  const dateKey = getDateKey(date);
  const events = readEvents(email);
  const dayEvents = { ...(events[dateKey] || {}) };
  const current = dayEvents[taskId] || {};
  dayEvents[taskId] = {
    ...current,
    [event]: true,
    updatedAt: new Date().toISOString(),
  };
  events[dateKey] = dayEvents;
  writeEvents(email, events);
};

export const getHomeTaskEvent = (email, taskId, date = new Date()) => {
  if (!email || !taskId) return {};
  const dateKey = getDateKey(date);
  return readEvents(email)[dateKey]?.[taskId] || {};
};

const getArticleExerciseTotal = (article) =>
  (article?.exercises?.length || 0) + (article?.rc_exercises?.length || 0);

const getArticleId = (article) =>
  article?.id || article?.article_id || article?.public_code;

export const getDailyVocabTaskProgress = (articles = [], email) => {
  if (!email || !Array.isArray(articles) || articles.length === 0) {
    return {
      status: HOME_TASK_STATUS.NOT_STARTED,
      completed: 0,
      total: 0,
    };
  }

  const openedArticles = readOpenedArticleIds(email);
  const allProgress = readArticleProgressMap(email);

  let completed = 0;
  let started = 0;

  articles.forEach((article) => {
    const articleId = getArticleId(article);
    const normalizedId = normalizeArticleId(articleId);
    const progress = getProgressForArticle(allProgress, articleId);
    const totalExercises = Math.max(
      progress.totalExercises || 0,
      getArticleExerciseTotal(article)
    );
    const completedIds = Array.isArray(progress.completedExerciseIds)
      ? Array.from(new Set(progress.completedExerciseIds.map(String)))
      : [];
    const exercisesCompleted = completedIds.length
      ? completedIds.length
      : progress.exercisesCompleted || 0;
    const isOpened = normalizedId ? openedArticles.has(normalizedId) : false;
    const isRead =
      progress.status === 'read' ||
      progress.status === 'completed' ||
      progress.status === 'done';
    let isComplete = false;
    if (totalExercises > 0) {
      isComplete = exercisesCompleted >= totalExercises;
    } else {
      // No exercises on this article: reading/opening counts toward daily completion.
      isComplete = isRead || isOpened;
    }

    if (isComplete) {
      completed += 1;
      started += 1;
    } else if (exercisesCompleted > 0 || isOpened) {
      started += 1;
    }
  });

  const total = articles.length;
  let status = HOME_TASK_STATUS.NOT_STARTED;
  if (total > 0 && completed >= total) {
    status = HOME_TASK_STATUS.COMPLETED;
  } else if (started > 0) {
    status = HOME_TASK_STATUS.IN_PROGRESS;
  }

  return { status, completed, total };
};

export const frozenGreExerciseTarget = (progress, exerciseType, fallback) => {
  const key =
    exerciseType === 'sentence_equivalence'
      ? 'sentence_equivalence_target_today'
      : 'text_completion_target_today';
  const target = progress?.[key];
  if (typeof target === 'number') return target;
  return typeof fallback === 'number' ? fallback : null;
};

export const statusFromCounts = (done, total) => {
  const safeDone = Math.max(0, Number(done) || 0);
  if (total === null || total === undefined) {
    return safeDone > 0
      ? HOME_TASK_STATUS.IN_PROGRESS
      : HOME_TASK_STATUS.NOT_STARTED;
  }
  const safeTotal = Math.max(0, Number(total) || 0);

  if (safeTotal === 0) {
    return safeDone > 0
      ? HOME_TASK_STATUS.IN_PROGRESS
      : HOME_TASK_STATUS.NOT_STARTED;
  }
  if (safeDone >= safeTotal) {
    return HOME_TASK_STATUS.COMPLETED;
  }
  if (safeDone > 0) {
    return HOME_TASK_STATUS.IN_PROGRESS;
  }
  return HOME_TASK_STATUS.NOT_STARTED;
};

export const statusFromFlags = (completed, inProgress) => {
  if (completed) return HOME_TASK_STATUS.COMPLETED;
  if (inProgress) return HOME_TASK_STATUS.IN_PROGRESS;
  return HOME_TASK_STATUS.NOT_STARTED;
};

export const fetchDailyArticlesForHome = async () => {
  const response = await fetch(`${API_BASE_URL}/api/gre/daily-articles`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(
      response.status === 401 ? 'Authentication failed' : 'Failed to fetch daily articles'
    );
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data?.articles || [];
};

const HOME_WINDOW_OFFSETS = [-1, 0, 1, 2];
const HOME_WINDOW_LABELS = {
  [-1]: 'hier',
  0: 'aujourd_hui',
  1: 'demain',
  2: 'apres_demain',
};

export const fetchGreProgramWindow = async () => {
  const response = await fetch(`${API_BASE_URL}/api/gre/program-window`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'Authentication failed'
        : 'Failed to fetch GRE program window'
    );
  }
  return response.json();
};

export const fetchJourneyProgramWindow = async () => {
  const response = await fetch(
    `${API_BASE_URL}/api/user/app/journey/program-window`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'Authentication failed'
        : 'Failed to fetch journey program window'
    );
  }
  return response.json();
};

export const loadHomeProgramWindows = async () => {
  const [greResult, journeyResult] = await Promise.allSettled([
    fetchGreProgramWindow(),
    fetchJourneyProgramWindow(),
  ]);
  return {
    greDays:
      greResult.status === 'fulfilled' ? greResult.value?.days || [] : [],
    journeyDays:
      journeyResult.status === 'fulfilled'
        ? journeyResult.value?.days || []
        : [],
    greFailed: greResult.status === 'rejected',
  };
};

export const mergeHomeProgramWindows = (greDays = [], journeyDays = []) =>
  HOME_WINDOW_OFFSETS.map((offset) => {
    const gre = (greDays || []).find((item) => item.offset === offset) || null;
    const journey =
      (journeyDays || []).find((item) => item.offset === offset) || null;
    if (!gre && !journey) return null;
    const greDone = !gre || gre.status === 'completed';
    const journeyDone = !journey || journey.status === 'completed';
    return {
      offset,
      label: gre?.label || journey?.label || HOME_WINDOW_LABELS[offset],
      date: gre?.date || journey?.date || null,
      status: greDone && journeyDone ? 'completed' : 'available',
      required: Boolean(
        (gre && gre.required !== false) ||
          (journey && journey.required !== false)
      ),
      program_day: journey?.program_day ?? gre?.program_day ?? null,
      items: Array.isArray(gre?.items) ? gre.items : [],
    };
  }).filter(Boolean);

export const resolveSelectedJourneyDay = (
  currentDay,
  offset = 0,
  planLength
) => {
  const selected = Number(currentDay) + Number(offset || 0);
  if (!Number.isFinite(selected) || selected < 1) return null;
  if (planLength && selected > planLength) return null;
  return selected;
};

export const articlesFromProgramWindow = (windowData, offset = 0) => {
  const day = (windowData?.days || []).find((item) => item.offset === offset);
  return Array.isArray(day?.items) ? day.items : [];
};

export const greVocabHomeTasksFromArticles = (articles = []) =>
  (Array.isArray(articles) ? articles : []).map((article, index) => {
    const ref = article?.public_code || article?.id || article?.article_id;
    return {
      id: `gre-article-${ref || index}`,
      label: article?.title || `Article ${index + 1}`,
      sublabel: 'Vocabulary article',
      path: ref
        ? `/exams/gre/verbal/vocabulary/dashboard/?article=${encodeURIComponent(ref)}`
        : '/exams/gre',
      state: { activeCategory: 'vocabularymastery' },
    };
  });

export const fetchHomeTodayProgress = async (dateKey) => {
  const response = await fetch(
    `${API_BASE_URL}/api/gre/home/today-progress?date=${encodeURIComponent(dateKey)}`,
    {
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'Authentication failed'
        : 'Failed to fetch today progress'
    );
  }
  return response.json();
};

export const getTaskItemClassName = (status, extraClass = '') => {
  const stateClass =
    status === HOME_TASK_STATUS.COMPLETED
      ? 'cc-is-completed'
      : status === HOME_TASK_STATUS.IN_PROGRESS
        ? 'cc-is-in-progress'
        : '';
  return ['cc-task-item', extraClass, stateClass].filter(Boolean).join(' ');
};

export const resolveHomeTaskStatus = ({
  task,
  email,
  date,
  dailyArticles,
  todayProgress,
  unlockedCount,
}) => {
  const events = getHomeTaskEvent(email, task.id, date);
  const progress = todayProgress || {};

  switch (task.id) {
    case 'gre-vocab': {
      return getDailyVocabTaskProgress(dailyArticles, email).status;
    }
    case 'gre-tc': {
      if (!todayProgress) return null;
      return statusFromCounts(
        progress.text_completion_attempted_today || 0,
        frozenGreExerciseTarget(progress, 'text_completion')
      );
    }
    case 'gre-se': {
      if (!todayProgress) return null;
      return statusFromCounts(
        progress.sentence_equivalence_attempted_today || 0,
        frozenGreExerciseTarget(progress, 'sentence_equivalence')
      );
    }
    case 'gre-reading':
    case 'weekend-reading': {
      return statusFromFlags(
        progress.reading_completed_today,
        progress.reading_in_progress_today
      );
    }
    case 'gre-flashcard-review':
    case 'weekend-flashcard': {
      return statusFromCounts(progress.flashcards_reviewed_today || 0, 1);
    }
    case 'gre-article-revision':
    case 'weekend-article-revision': {
      return statusFromFlags(progress.article_revised_today, events.started);
    }
    case 'gre-quant-course':
    case 'weekend-quant': {
      return statusFromFlags(
        progress.quant_lesson_completed_today,
        progress.quant_lesson_attempted_today
      );
    }
    case 'gre-quant-exercises': {
      return statusFromFlags(
        progress.quant_practice_completed_today,
        progress.quant_practice_started_today
      );
    }
    case 'gre-mock-test':
    case 'gre-mock-test-priority':
    case 'mandatory-gre-diagnostic': {
      if (task.preCompleted) return HOME_TASK_STATUS.COMPLETED;
      return statusFromFlags(
        progress.mock_test_completed_today,
        progress.mock_test_in_progress_today || events.started
      );
    }
    case 'gre-vocab-challenge':
    case 'weekend-vocab':
    case 'gre-nuance-review':
    case 'weekend-nuance': {
      return statusFromFlags(events.finished, events.started && !events.finished);
    }
    case 'mandatory-ielts-assessment': {
      if (task.preCompleted) return HOME_TASK_STATUS.COMPLETED;
      return statusFromFlags(false, events.started);
    }
    default: {
      if (String(task.id).startsWith('gre-article-')) {
        const articleRef = String(task.id).slice('gre-article-'.length);
        const article = (dailyArticles || []).find((item) => {
          const id = getArticleId(item);
          return (
            String(id) === articleRef ||
            String(item?.public_code || '') === articleRef
          );
        });
        return getDailyVocabTaskProgress(article ? [article] : [], email).status;
      }
      if (task.preCompleted) return HOME_TASK_STATUS.COMPLETED;
      if (String(task.id).startsWith('ielts-')) {
        return statusFromFlags(false, events.started);
      }
      if (
        String(task.id).startsWith('mba-') ||
        String(task.id).startsWith('application-') ||
        task.id === 'priority-deadline'
      ) {
        if (task.preCompleted) return HOME_TASK_STATUS.COMPLETED;
        return statusFromFlags(events.finished, events.started);
      }
      return statusFromFlags(events.finished, events.started);
    }
  }
};
